import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { ECurecallNotificationAppearance } from '@honestica/curecall-ui'
import {
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type Edge,
  type EdgeChange,
  type NodeChange,
} from '@xyflow/react'
import {
  type TChatbotNode,
  type TChatbotNodeData,
  type TNodeAction,
  type TNodeFormValues,
  type TRange,
} from './node.type'
import { convertApiNodesToReactFlow, convertNodesToApiFormat } from './chatbot-api.helper'
import { ChatbotService } from 'src/services/chatbot.service'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNotification } from 'src/providers/notification.provider'
import { useTranslation } from 'react-i18next'
import { useProjectVariables } from './chatbot-form/use-project-variables.hook'
import { useDebouncedCallback } from 'use-debounce'
import {
  DEFAULT_NODE_TYPE,
  DEFAULT_EDGE_TYPE,
  DEFAULT_LOCALE,
  INITIAL_NODE_POSITION,
  NODE_VERTICAL_SPACING,
  START_NODE_ID,
  FIRST_EDITABLE_NODE_ID,
  INITIAL_NODES,
  INITIAL_EDGES,
} from './chatbot.constants'
import { createIdGenerators } from './chatbot-id.helper'
import { EHumanReviewRequestSubject } from 'src/pages/dashboard/_components/human-review-requests.type'
import { calculateNodePosition, calculateConditionalNodePosition } from './chatbot-position.helper'
import {
  updateBooleanCondition,
  updateIntegerRangeAction as updateIntegerRangeActionHelper,
  updateIntegerRanges,
} from './chatbot-condition.helper'

type TChatbotFlowContext = {
  isChatbotPublished: boolean
  hasUnsavedChanges: boolean
  chatbotName: string
  followupId: number
  chatbotId: number
  nodes: TChatbotNode[]
  edges: Edge[]
  isFormOpen: boolean
  selectedNodeId: string | null
  addNode: (data: TNodeFormValues) => void
  addNodeWithConnection: (parentNodeId: string) => void
  addNodeWithConditionalConnection: (parentNodeId: string, conditionKey: string) => void
  updateNode: (id: string, data: TNodeFormValues) => void
  updateCurrentNodeData: (data: Partial<TChatbotNodeData>) => void
  removeEdgesFromHandle: (nodeId: string, handleId: string) => void
  updateBooleanConditionAction: (conditionKey: 'yes' | 'no', action: Partial<TNodeAction>) => void
  updateIntegerRangeAction: (rangeIndex: number, action: Partial<TNodeAction>) => void
  updateIntegerRangeConditionAction: (ranges: TRange[]) => void
  deleteNode: (id: string) => void
  copyNode: (id: string) => void
  pasteNode: () => void
  openFormForNode: (id: string) => void
  openForm: () => void
  closeForm: () => void
  onNodesChange: (changes: NodeChange<TChatbotNode>[]) => void
  onEdgesChange: (changes: EdgeChange<Edge>[]) => void
  onConnect: (connection: Connection) => void
  saveChatbot: () => Promise<void>
  saveAndKeepPublished: (options?: { silent?: boolean }) => Promise<void>
  publishChatbot: () => Promise<void>
  unpublishChatbot: () => Promise<void>
  togglePublishChatbot: () => Promise<void>
  setFormDirty: (isDirty: boolean) => void
  dashboardSubject: EHumanReviewRequestSubject | null
}
const ChatbotFlowContext = createContext<TChatbotFlowContext>({} as TChatbotFlowContext)
export const useChatbotFlow = () => useContext(ChatbotFlowContext) as TChatbotFlowContext

export function ChatbotFlowProvider({
  children,
  projectId,
  followupId,
  chatbotId,
}: Readonly<{
  children: ReactNode
  projectId: number
  followupId: number
  chatbotId: number
}>) {
  const { t } = useTranslation()
  const { notify } = useNotification()
  const queryClient = useQueryClient()
  const [nodes, setNodes] = useState<TChatbotNode[]>(INITIAL_NODES)
  const [edges, setEdges] = useState<Edge[]>(INITIAL_EDGES)
  const [isFormOpen, setIsFormOpen] = useState(true)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(FIRST_EDITABLE_NODE_ID)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [copiedNodeData, setCopiedNodeData] = useState<{
    data: TChatbotNodeData
    position: { x: number; y: number }
  } | null>(null)

  useEffect(() => {
    if (!hasUnsavedChanges) return
    notify(
      {
        title: t('pages.configuration.followups.chatbot.editor.unsaved-changes-warning-title'),
        message: t('pages.configuration.followups.chatbot.editor.unsaved-changes-warning-message'),
        appearance: ECurecallNotificationAppearance.WARNING,
      },
      30000,
    )
  }, [hasUnsavedChanges, notify, t])

  // Use refs instead of state to avoid batching issues with ReactFlow callbacks
  const isInitialLoadDoneRef = useRef(false)
  const hasUserMadeChangesRef = useRef(false)

  // Create ID generators instance per component to avoid ID collisions between chatbots
  const idGeneratorsRef = useRef(createIdGenerators())
  const { getNodeId, getEdgeId, initializeCounters } = idGeneratorsRef.current

  const { data: chatbotData } = useQuery({
    queryKey: ['chatbot', chatbotId],
    queryFn: () => ChatbotService.get(projectId, followupId, chatbotId),
    refetchOnMount: 'always', // Always fetch fresh data when component mounts to prevent stale cache
    gcTime: 0, // Don't cache data - always fetch from API to prevent serving stale data
  })

  const { data: projectVariables = [] } = useProjectVariables()

  const publishMutation = useMutation({
    mutationFn: (isActive: boolean) => ChatbotService.publish(projectId, followupId, chatbotId, isActive),
    onSuccess: (_, isActive) => {
      // Update query data directly instead of invalidating to prevent re-fetch during editing
      queryClient.setQueryData(['chatbot', chatbotId], (oldData: typeof chatbotData) => {
        if (!oldData) return oldData
        return { ...oldData, isActive }
      })
      notify({
        title: t('common.actions.success'),
        message: isActive
          ? t('pages.configuration.followups.chatbot.publish-success')
          : t('pages.configuration.followups.chatbot.unpublish-success'),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
    },
  })

  useEffect(() => {
    // Only load from API once on initial mount, ignore subsequent chatbotData changes
    // to prevent auto-save from overwriting local edits
    if (isInitialLoadDoneRef.current || !chatbotData?.nodes || projectVariables.length === 0) {
      return
    }

    const { nodes: loadedNodes, edges: loadedEdges } = convertApiNodesToReactFlow(
      chatbotData.nodes,
      projectVariables,
    )

    // If API only has the Start node (newly created chatbot with no content),
    // keep INITIAL_NODES instead of replacing them
    const firstEditableNode = loadedNodes.find((node) => node.data.isEditable !== false)

    if (!firstEditableNode) {
      // No editable nodes from API = newly created chatbot, keep INITIAL_NODES
      isInitialLoadDoneRef.current = true
      return
    }

    // API has real content, load it
    // Mark the first editable node as selected
    const nodesWithSelection = loadedNodes.map((node) => ({
      ...node,
      selected: node.id === firstEditableNode.id,
    }))

    setNodes(nodesWithSelection)
    setEdges(loadedEdges)
    setSelectedNodeId(firstEditableNode.id)
    setIsFormOpen(true)

    // Update counters to prevent ID collisions
    initializeCounters(loadedNodes, loadedEdges)

    // Mark loading as complete AFTER setting nodes/edges
    // Ref is synchronous and won't cause re-render
    isInitialLoadDoneRef.current = true
  }, [chatbotData, projectVariables, initializeCounters])

  const addNode = useCallback(
    (data: TNodeFormValues) => {
      const newNode: TChatbotNode = {
        id: getNodeId(),
        type: DEFAULT_NODE_TYPE,
        position: {
          x: INITIAL_NODE_POSITION.x,
          y: INITIAL_NODE_POSITION.y + nodes.length * NODE_VERTICAL_SPACING,
        },
        data: {
          title: data.title,
          messagesByLanguage: data.messagesByLanguage,
          variable: data.variable,
          humanReviewCheck: data.humanReviewCheck,
        },
      }
      setNodes((nds) => [...nds, newNode])
      setIsFormOpen(false)
      setSelectedNodeId(null)
      hasUserMadeChangesRef.current = true

      // Mark as having unsaved changes if published
      if (chatbotData?.isActive) {
        setHasUnsavedChanges(true)
      }
    },
    [nodes.length, chatbotData?.isActive, getNodeId],
  )

  const addNodeWithConnection = useCallback(
    (parentNodeId: string) => {
      const parentNode = nodes.find((n) => n.id === parentNodeId)
      if (!parentNode) return

      const newNodeId = getNodeId()
      const position = calculateNodePosition(parentNode, nodes)

      const newNode: TChatbotNode = {
        id: newNodeId,
        type: DEFAULT_NODE_TYPE,
        position,
        data: {
          title: '',
          messagesByLanguage: { [DEFAULT_LOCALE]: '' },
        },
        selected: true,
      }

      const newEdge: Edge = {
        id: getEdgeId(),
        source: parentNodeId,
        target: newNodeId,
        type: DEFAULT_EDGE_TYPE,
      }

      // Deselect all existing nodes and add the new selected node
      setNodes((nds) => [...nds.map((n) => (n.selected ? { ...n, selected: false } : n)), newNode])
      setEdges((eds) => [...eds, newEdge])
      setSelectedNodeId(newNodeId)
      setIsFormOpen(true)
      hasUserMadeChangesRef.current = true
    },
    [nodes, getNodeId, getEdgeId],
  )

  const addNodeWithConditionalConnection = useCallback(
    (parentNodeId: string, conditionKey: string) => {
      const parentNode = nodes.find((n) => n.id === parentNodeId)
      if (!parentNode) return

      const newNodeId = getNodeId()
      const position = calculateConditionalNodePosition(parentNode, conditionKey, nodes, edges, parentNodeId)

      const newNode: TChatbotNode = {
        id: newNodeId,
        type: DEFAULT_NODE_TYPE,
        position,
        data: {
          title: '',
          messagesByLanguage: { [DEFAULT_LOCALE]: '' },
        },
        selected: true,
      }

      const newEdge: Edge = {
        id: getEdgeId(),
        source: parentNodeId,
        sourceHandle: conditionKey,
        target: newNodeId,
        type: DEFAULT_EDGE_TYPE,
      }

      // Deselect all existing nodes and add the new selected node
      setNodes((nds) => [...nds.map((n) => (n.selected ? { ...n, selected: false } : n)), newNode])
      setEdges((eds) => [...eds, newEdge])
      setSelectedNodeId(newNodeId)
      setIsFormOpen(true)
      hasUserMadeChangesRef.current = true
    },
    [nodes, edges, getNodeId, getEdgeId],
  )

  const updateNode = useCallback(
    (id: string, data: TNodeFormValues) => {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  title: data.title,
                  messagesByLanguage: data.messagesByLanguage,
                  variable: data.variable,
                  humanReviewCheck: data.humanReviewCheck,
                },
                selected: false,
              }
            : { ...node, selected: false },
        ),
      )
      setIsFormOpen(false)
      setSelectedNodeId(null)
      hasUserMadeChangesRef.current = true

      // Mark as having unsaved changes if published
      if (chatbotData?.isActive) {
        setHasUnsavedChanges(true)
      }
    },
    [chatbotData?.isActive],
  )

  const updateNodeData = useCallback(
    (id: string, data: Partial<TChatbotNodeData>) => {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === id
            ? {
                ...node,
                data: {
                  ...node.data,
                  ...data,
                },
              }
            : node,
        ),
      )
      hasUserMadeChangesRef.current = true

      // Mark as having unsaved changes if published
      if (chatbotData?.isActive) {
        setHasUnsavedChanges(true)
      }
    },
    [chatbotData?.isActive],
  )

  const updateCurrentNodeData = useCallback(
    (data: Partial<TChatbotNodeData>) => {
      if (!selectedNodeId) return
      updateNodeData(selectedNodeId, data)
    },
    [selectedNodeId, updateNodeData],
  )

  const removeEdgesFromHandle = useCallback(
    (nodeId: string, handleId: string) => {
      setEdges((eds) => eds.filter((edge) => !(edge.source === nodeId && edge.sourceHandle === handleId)))
      hasUserMadeChangesRef.current = true
      if (chatbotData?.isActive) {
        setHasUnsavedChanges(true)
      }
    },
    [chatbotData?.isActive],
  )

  const updateBooleanConditionAction = useCallback(
    (conditionKey: 'yes' | 'no', action: Partial<TNodeAction>) => {
      if (!selectedNodeId) return

      const currentNode = nodes.find((n) => n.id === selectedNodeId)
      const currentVariable = currentNode?.data.variable
      if (!currentVariable) return

      const updatedVariable = updateBooleanCondition(currentVariable, conditionKey, action)
      updateNodeData(selectedNodeId, { variable: updatedVariable })
    },
    [selectedNodeId, nodes, updateNodeData],
  )

  const updateIntegerRangeAction = useCallback(
    (rangeIndex: number, action: Partial<TNodeAction>) => {
      if (!selectedNodeId) return

      const currentNode = nodes.find((n) => n.id === selectedNodeId)
      const currentVariable = currentNode?.data.variable
      if (!currentVariable) return

      const updatedVariable = updateIntegerRangeActionHelper(currentVariable, rangeIndex, action)
      updateNodeData(selectedNodeId, { variable: updatedVariable })
    },
    [selectedNodeId, nodes, updateNodeData],
  )

  const updateIntegerRangeConditionAction = useCallback(
    (ranges: TRange[]) => {
      if (!selectedNodeId) return

      const currentNode = nodes.find((n) => n.id === selectedNodeId)
      const currentVariable = currentNode?.data.variable
      if (!currentVariable) return

      const updatedVariable = updateIntegerRanges(currentVariable, ranges)
      updateNodeData(selectedNodeId, { variable: updatedVariable })
    },
    [selectedNodeId, nodes, updateNodeData],
  )

  const copyNode = useCallback(
    (id: string) => {
      if (id === START_NODE_ID) return
      const node = nodes.find((n) => n.id === id)
      if (!node) return
      setCopiedNodeData({ data: node.data, position: node.position })
    },
    [nodes],
  )

  const pasteNode = useCallback(() => {
    if (!copiedNodeData) return
    const newNodeId = getNodeId()
    const newNode: TChatbotNode = {
      id: newNodeId,
      type: DEFAULT_NODE_TYPE,
      position: {
        x: copiedNodeData.position.x + 50,
        y: copiedNodeData.position.y + 50,
      },
      data: { ...copiedNodeData.data },
      selected: true,
    }
    setNodes((nds) => [...nds.map((n) => ({ ...n, selected: false })), newNode])
    setSelectedNodeId(newNodeId)
    setIsFormOpen(true)
    hasUserMadeChangesRef.current = true
    if (chatbotData?.isActive) {
      setHasUnsavedChanges(true)
    }
  }, [copiedNodeData, getNodeId, chatbotData?.isActive])

  const deleteNode = useCallback(
    (id: string) => {
      // Prevent deleting the initial node
      if (id === START_NODE_ID) return

      setNodes((nds) => nds.filter((node) => node.id !== id))
      setEdges((eds) => eds.filter((edge) => edge.source !== id && edge.target !== id))

      // Close form if the deleted node is the selected one
      if (id === selectedNodeId) {
        setIsFormOpen(false)
        setSelectedNodeId(null)
      }

      hasUserMadeChangesRef.current = true

      // Mark as having unsaved changes if published
      if (chatbotData?.isActive) {
        setHasUnsavedChanges(true)
      }
    },
    [chatbotData?.isActive, selectedNodeId],
  )

  const openFormForNode = useCallback((id: string) => {
    // Prevent opening form for the start node (not editable)
    if (id === START_NODE_ID) return

    // Deselect all nodes and select only the clicked node
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        selected: node.id === id,
      })),
    )

    setSelectedNodeId(id)
    setIsFormOpen(true)
  }, [])

  const openForm = useCallback(() => {
    // Reopen the form - if there's a selectedNodeId, keep it selected
    if (selectedNodeId) {
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          selected: node.id === selectedNodeId,
        })),
      )
    }
    setIsFormOpen(true)
  }, [selectedNodeId])

  const closeForm = useCallback(() => {
    // Keep the node selected, just close the form panel
    setIsFormOpen(false)
  }, [])

  const onNodesChange = useCallback(
    (changes: NodeChange<TChatbotNode>[]) => {
      // Check if the selected node is being removed
      const removedNodes = changes.filter((change) => change.type === 'remove')
      if (removedNodes.some((change) => change.id === selectedNodeId)) {
        setIsFormOpen(false)
        setSelectedNodeId(null)
      }

      // Mark as user made changes only if:
      // 1. Initial load is done (prevent programmatic changes during load from triggering auto-save)
      // 2. Changes are NOT just position or dimension updates (internal ReactFlow updates)
      const hasNonPositionChanges = changes.some(
        (change) => change.type !== 'position' && change.type !== 'dimensions',
      )

      if (isInitialLoadDoneRef.current && hasNonPositionChanges) {
        hasUserMadeChangesRef.current = true
      }

      setNodes((nds) => applyNodeChanges(changes, nds))
    },
    [selectedNodeId],
  )

  const onEdgesChange = useCallback(
    (changes: EdgeChange<Edge>[]) => {
      setEdges((eds) => applyEdgeChanges(changes, eds))

      // Mark as user made changes only if:
      // 1. Initial load is done (prevent programmatic changes during load from triggering auto-save)
      // 2. Changes are NOT just select updates (internal ReactFlow updates)
      const hasRealChanges = changes.some((change) => change.type !== 'select')

      if (isInitialLoadDoneRef.current && hasRealChanges) {
        hasUserMadeChangesRef.current = true
        if (chatbotData?.isActive) {
          setHasUnsavedChanges(true)
        }
      }
    },
    [chatbotData?.isActive],
  )

  const onConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return

      setEdges((eds) => [
        ...eds,
        {
          id: getEdgeId(),
          source: connection.source,
          sourceHandle: connection.sourceHandle,
          target: connection.target,
          type: DEFAULT_EDGE_TYPE,
        },
      ])
      if (isInitialLoadDoneRef.current) {
        hasUserMadeChangesRef.current = true
        if (chatbotData?.isActive) {
          setHasUnsavedChanges(true)
        }
      }
    },
    [getEdgeId, chatbotData?.isActive],
  )

  const saveChatbot = useCallback(async () => {
    // Skip API call if published - changes are tracked locally
    if (chatbotData?.isActive) {
      return
    }

    try {
      // Auto-save for unpublished chatbots (existing behavior)
      const conversationNodes = convertNodesToApiFormat(nodes, edges)
      await ChatbotService.update(projectId, followupId, chatbotId, {
        nodes: conversationNodes,
      })
    } catch (error) {
      notify({
        title: t('common.actions.error'),
        message: t('pages.configuration.followups.chatbot.save.error'),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
      console.error('Failed to auto-save chatbot:', error)
    }
  }, [nodes, edges, projectId, followupId, chatbotId, chatbotData?.isActive, notify, t])

  const saveAndKeepPublished = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      const conversationNodes = convertNodesToApiFormat(nodes, edges)
      await ChatbotService.update(projectId, followupId, chatbotId, {
        nodes: conversationNodes,
      })
      // Update query data directly instead of invalidating to prevent re-fetch during editing
      queryClient.setQueryData(['chatbot', chatbotId], (oldData: any) => {
        if (!oldData) return oldData
        return { ...oldData, nodes: conversationNodes }
      })
      setHasUnsavedChanges(false)
      if (!silent) {
        notify({
          title: t('common.actions.success'),
          message: t('pages.configuration.followups.chatbot.save.success'),
          appearance: ECurecallNotificationAppearance.SUCCESS,
        })
      }
    },
    [nodes, edges, projectId, followupId, chatbotId, queryClient, notify, t],
  )

  const publishChatbot = useCallback(async () => {
    // If publishing with unsaved changes, save them first (silently — publish notification is enough)
    if (hasUnsavedChanges) {
      await saveAndKeepPublished({ silent: true })
    }
    await publishMutation.mutateAsync(true)
  }, [publishMutation, hasUnsavedChanges, saveAndKeepPublished])

  const unpublishChatbot = useCallback(async () => {
    // If unpublishing with unsaved changes, save them first (silently — unpublish notification is enough)
    if (hasUnsavedChanges) {
      await saveAndKeepPublished({ silent: true })
    }
    await publishMutation.mutateAsync(false)
    setHasUnsavedChanges(false)
  }, [publishMutation, hasUnsavedChanges, saveAndKeepPublished])

  const togglePublishChatbot = useCallback(async () => {
    const isCurrentlyPublished = chatbotData?.isActive

    // If unpublishing with unsaved changes, save them first (silently — publish notification is enough)
    if (isCurrentlyPublished && hasUnsavedChanges) {
      await saveAndKeepPublished({ silent: true })
    }

    await publishMutation.mutateAsync(!isCurrentlyPublished)

    // Reset unsaved changes flag when unpublishing
    if (isCurrentlyPublished) {
      setHasUnsavedChanges(false)
    }
  }, [publishMutation, chatbotData?.isActive, hasUnsavedChanges, saveAndKeepPublished])

  const setFormDirty = useCallback(
    (isDirty: boolean) => {
      // Only mark as unsaved if chatbot is published and form is dirty
      if (chatbotData?.isActive && isDirty) {
        setHasUnsavedChanges(true)
      }
    },
    [chatbotData?.isActive],
  )

  // Auto-save system: debounce (3s) + throttle (20s max) using useDebouncedCallback
  // - trailing: true → save after the delay (debounce behavior)
  // - maxWait: 30000 → guarantee a save at least every 30s (throttle behavior)
  const debouncedSave = useDebouncedCallback(
    () => {
      saveChatbot()
    },
    3000,
    { trailing: true, maxWait: 30000 },
  )

  useEffect(() => {
    // Skip if still loading initial data from API
    if (!isInitialLoadDoneRef.current) {
      return
    }

    // Skip if chatbot is published (changes are tracked locally, not auto-saved)
    if (chatbotData?.isActive) {
      return
    }

    // Skip if user hasn't made any changes yet (prevent auto-save on initial load)
    if (!hasUserMadeChangesRef.current) {
      return
    }

    // Trigger the debounced save on every nodes/edges change
    debouncedSave()

    // Cleanup: cancel pending debounced call on unmount to prevent memory leaks
    return () => {
      debouncedSave.cancel()
    }
  }, [nodes, edges, chatbotData?.isActive, debouncedSave])

  const value: TChatbotFlowContext = {
    isChatbotPublished: chatbotData?.isActive || false,
    hasUnsavedChanges,
    chatbotName: chatbotData?.name || '',
    followupId,
    chatbotId,
    nodes,
    edges,
    isFormOpen,
    selectedNodeId,
    addNode,
    addNodeWithConnection,
    addNodeWithConditionalConnection,
    updateNode,
    updateCurrentNodeData,
    removeEdgesFromHandle,
    updateBooleanConditionAction,
    updateIntegerRangeAction,
    updateIntegerRangeConditionAction,
    deleteNode,
    copyNode,
    pasteNode,
    openFormForNode,
    openForm,
    closeForm,
    onNodesChange,
    onEdgesChange,
    onConnect,
    saveChatbot,
    saveAndKeepPublished,
    publishChatbot,
    unpublishChatbot,
    togglePublishChatbot,
    setFormDirty,
    dashboardSubject: chatbotData?.options?.humanReviewConfig?.subject ?? null,
  }

  return <ChatbotFlowContext.Provider value={value}>{children}</ChatbotFlowContext.Provider>
}
